/**
 * useTasks — TanStack Query hooks for Tasks.
 * Spec 61 Phase A5a.
 *
 * Every request and every cache key is the generated one (`xOptions` / `xQueryKey` / `xMutation`).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  approveTaskActionMutation,
  cancelTaskMutation,
  completeTaskMutation,
  createTaskMutation,
  deleteTaskMutation,
  extendApprovalTimeoutMutation,
  getTaskOptions,
  getTaskQueryKey,
  listTaskDecisionsOptions,
  listTasksOptions,
  listTasksQueryKey,
  reassignTaskMutation,
  rejectTaskActionMutation,
  retryTaskMutation,
  startTaskMutation,
  transferTaskTeamMutation,
  updateTaskMutation,
} from '../generated/api/@tanstack/react-query.gen.js';
import type {
  ApproveTaskActionData,
  CompleteTaskData,
  CreateTaskData,
  ListTasksData,
  ReassignTaskData,
  RejectTaskActionData,
  Task,
  TaskDecision,
  TaskList,
  TransferTaskTeamData,
  UpdateTaskData,
  WorkflowContract,
} from '../generated/api/types.gen.js';
import { adaptMutation } from './adapt-mutation.js';

// ─── Types derived from the generated contract ───────────────────────────────

export type TaskStatus = Task['status'];
export type TaskFailureReason = NonNullable<Task['failureReason']>;
export type TaskOriginType = Task['originType'];
export type TaskAssignmentType = Task['assigneeType'];
export type TaskContract = WorkflowContract;

export type { Task };

// Re-export generated TaskDecision type for consumers.
export type { TaskDecision };

export type TaskListResponse = TaskList;
export type TaskFilters = NonNullable<ListTasksData['query']>;

const taskPath = (id: number | string) => ({ path: { id: String(id) } });

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useTasksList(filters: TaskFilters = {}) {
  const query = useQuery(listTasksOptions({ query: filters }));
  if (query.isError) console.error('useTasksList: failed to load tasks', query.error);
  return query;
}

export function useTask(id: number | string | undefined) {
  return useQuery({ ...getTaskOptions(taskPath(id ?? '')), enabled: !!id });
}

export function useTaskDecisions(taskId: number | string | undefined) {
  return useQuery({
    ...listTaskDecisionsOptions(taskPath(taskId ?? '')),
    enabled: !!taskId,
    // Refresh every 10 seconds while the approval panel is visible to keep
    // the decision list fresh as the agent may add new pending decisions.
    refetchInterval: 10_000,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

/** The task changed: refresh its detail and every list. */
function useRefreshTask() {
  const queryClient = useQueryClient();
  return (id: number | string) => {
    queryClient.invalidateQueries({ queryKey: getTaskQueryKey(taskPath(id)) });
    queryClient.invalidateQueries({ queryKey: listTasksQueryKey() });
  };
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(createTaskMutation(), (body: NonNullable<CreateTaskData['body']>) => ({ body })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTasksQueryKey() });
    },
  });
}

export function useUpdateTask() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(
      updateTaskMutation(),
      ({ id, patch }: { id: number | string; patch: NonNullable<UpdateTaskData['body']> }) => ({
        ...taskPath(id),
        body: patch,
      }),
    ),
    onSuccess: (_data, { id }) => refresh(id),
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    ...adaptMutation(deleteTaskMutation(), (id: number | string) => taskPath(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTasksQueryKey() });
    },
  });
}

// ─── Action mutations ─────────────────────────────────────────────────────────

export function useStartTask() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(startTaskMutation(), (id: number | string) => taskPath(id)),
    onSuccess: (_data, id) => refresh(id),
  });
}

export function useCancelTask() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(cancelTaskMutation(), (id: number | string) => taskPath(id)),
    onSuccess: (_data, id) => refresh(id),
  });
}

export function useApproveTaskAction() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(
      approveTaskActionMutation(),
      ({ id, ...body }: { id: number | string } & NonNullable<ApproveTaskActionData['body']>) => ({
        ...taskPath(id),
        body,
      }),
    ),
    onSuccess: (_data, { id }) => refresh(id),
  });
}

export function useRejectTaskAction() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(
      rejectTaskActionMutation(),
      ({ id, ...body }: { id: number | string } & NonNullable<RejectTaskActionData['body']>) => ({
        ...taskPath(id),
        body,
      }),
    ),
    onSuccess: (_data, { id }) => refresh(id),
  });
}

export function useExtendApprovalTimeout() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(extendApprovalTimeoutMutation(), (id: number | string) => taskPath(id)),
    onSuccess: (_data, id) => refresh(id),
  });
}

export function useCompleteTask() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(
      completeTaskMutation(),
      ({ id, ...body }: { id: number | string } & NonNullable<CompleteTaskData['body']>) => ({
        ...taskPath(id),
        body,
      }),
    ),
    onSuccess: (_data, { id }) => refresh(id),
  });
}

export function useRetryTask() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(retryTaskMutation(), (id: number | string) => taskPath(id)),
    onSuccess: (_data, id) => refresh(id),
  });
}

export function useReassignTask() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(
      reassignTaskMutation(),
      ({ id, ...body }: { id: number | string } & NonNullable<ReassignTaskData['body']>) => ({
        ...taskPath(id),
        body,
      }),
    ),
    onSuccess: (_data, { id }) => refresh(id),
  });
}

export function useTransferTaskTeam() {
  const refresh = useRefreshTask();
  return useMutation({
    ...adaptMutation(
      transferTaskTeamMutation(),
      ({ id, toTeamId }: { id: number | string; toTeamId: number | string }) => ({
        ...taskPath(id),
        body: { toTeamId: Number(toTeamId) } satisfies NonNullable<TransferTaskTeamData['body']>,
      }),
    ),
    onSuccess: (_data, { id }) => refresh(id),
  });
}
